import { i18n } from './i18n.js';

const DEBUG = import.meta.env.DEV;

// Default map center (Denmark)
const DEFAULT_CENTER = [56.0, 10.0];
const DEFAULT_ZOOM = 7;

// Spot type colors
const SPOT_TYPE_COLORS = {
  lake: '#2196F3',   // Blue
  sea: '#4CAF50',    // Green
  river: '#8B4513',  // Brown
};

class MapManager {
  constructor(containerId) {
    this.containerId = containerId;
    this.map = null;
    this.markers = new Map();
    this.currentBounds = null;
    this.boundsChangeCallback = null;
  }

  init() {
    if (DEBUG) {
      console.log('🗺️ Initializing map');
    }

    // Create map
    this.map = L.map(this.containerId).setView(DEFAULT_CENTER, DEFAULT_ZOOM);

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(this.map);

    // Handle map events
    this.map.on('moveend', () => this.onMapChange());
    this.map.on('zoomend', () => this.onMapChange());

    if (DEBUG) {
      console.log('✓ Map initialized');
    }
  }

  onMapChange() {
    const bounds = this.map.getBounds();
    const newBounds = {
      minLat: bounds.getSouth(),
      minLng: bounds.getWest(),
      maxLat: bounds.getNorth(),
      maxLng: bounds.getEast(),
    };

    if (DEBUG) {
      console.log('🔄 Map bounds changed:', newBounds);
    }

    this.currentBounds = newBounds;

    if (this.boundsChangeCallback) {
      this.boundsChangeCallback(newBounds);
    }
  }

  onBoundsChange(callback) {
    this.boundsChangeCallback = callback;
  }

  getCurrentBounds() {
    if (!this.currentBounds) {
      const bounds = this.map.getBounds();
      this.currentBounds = {
        minLat: bounds.getSouth(),
        minLng: bounds.getWest(),
        maxLat: bounds.getNorth(),
        maxLng: bounds.getEast(),
      };
    }
    return this.currentBounds;
  }

  createMarker(spot) {
    const [lng, lat] = spot.location.coordinates;
    const color = SPOT_TYPE_COLORS[spot.spotType] || SPOT_TYPE_COLORS.lake;

    // Create custom marker element
    const markerHtml = `
      <div class="marker-icon marker-${spot.spotType}" style="background-color: ${color}">
        🎣
      </div>
    `;

    const marker = L.marker([lat, lng], {
      icon: L.divIcon({
        html: markerHtml,
        iconSize: [30, 40],
        iconAnchor: [15, 40],
        popupAnchor: [0, -40],
      }),
    });

    // Create popup content
    const popupHtml = `
      <div class="popup-title">${this.escapeHtml(spot.name)}</div>
      <div class="popup-content">${this.escapeHtml(spot.description.substring(0, 100))}...</div>
      <a href="/spot/${spot.id}" class="popup-button">${i18n.t('map.viewDetails')}</a>
    `;

    marker.bindPopup(popupHtml);

    return marker;
  }

  addSpots(spots) {
    if (DEBUG) {
      console.log(`📍 Adding ${spots.length} spots to map`);
    }

    spots.forEach(spot => {
      // Skip if marker already exists
      if (this.markers.has(spot.id)) {
        return;
      }

      const marker = this.createMarker(spot);
      marker.addTo(this.map);
      this.markers.set(spot.id, marker);
    });

    if (DEBUG) {
      console.log(`✓ Total markers on map: ${this.markers.size}`);
    }
  }

  clearSpots() {
    if (DEBUG) {
      console.log('🗑️ Clearing all markers');
    }

    this.markers.forEach(marker => {
      this.map.removeLayer(marker);
    });
    this.markers.clear();
  }

  centerOn(spot) {
    const [lng, lat] = spot.location.coordinates;
    this.map.setView([lat, lng], 12);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  getCurrentBoundsForQuery() {
    const bounds = this.getCurrentBounds();
    return {
      minLat: Math.round(bounds.minLat * 1000) / 1000,
      minLng: Math.round(bounds.minLng * 1000) / 1000,
      maxLat: Math.round(bounds.maxLat * 1000) / 1000,
      maxLng: Math.round(bounds.maxLng * 1000) / 1000,
    };
  }
}

export { MapManager, SPOT_TYPE_COLORS };
